/**
 * ColyseusDriver — the real TableDriver over @colyseus/sdk 0.18 (DESIGN-server §3.2, §3.3,
 * §3.5, §4 catch-up; DESIGN-client §1.2).
 *
 * - Endpoint `${origin}${netBase()}` (Discord: `/.proxy`), auth via `client.auth.token`.
 * - Discord: `joinOrCreate('table', { surface:'discord', instanceId, guildId, channelId })`.
 *   Web: quick play `create('table', { surface:'web', solo:true })`; code → `GET /api/rooms/:code`
 *   → `joinById`; private → `POST /api/rooms` → `joinById`.
 * - Schema → plain `TableView` (`state.toJSON()`) on every patch.
 * - `cue` / `private` / `cues` / `error` / `progress` messages fan out to listeners.
 * - Reconnection: `room.reconnectionToken` in sessionStorage `sn.reconn`; the SDK's built-in
 *   drop/reconnect is used first (`onDrop` → 'reconnecting', `onReconnect` → `resync`), then a
 *   manual `client.reconnect(token)` loop after `onLeave`, then 'lost'.
 */
import { Client, type Room } from '@colyseus/sdk';
import { ProgressMessageSchema } from '@spades/shared';
import { apiPath, netOrigin } from '../bridge/embedded';
import type { ConnectOptions, DriverConnection, ResyncKind, TableDriver } from './TableDriver';
import {
  type Cue,
  type CuesMessage,
  type ErrorMessage,
  type Intents,
  type PrivateMessage,
  type ProgressMessage,
  type SeatView,
  type TableView,
  SOCKET_ERROR_CODE,
  emptyTableView,
} from './types-local';

export const RECONNECTION_STORAGE_KEY = 'sn.reconn';

/**
 * Close codes that mean "this room is finished with you", so reconnecting is pointless: 1000 is the
 * plain websocket close nobody sends here, 4000 is Colyseus's consented leave, 4001 is our
 * superseded-by-another-tab close, and 4002 is a server shutdown.
 */
export const DELIBERATE_CLOSE_CODES: readonly number[] = [1000, 4000, 4001, 4002];

/**
 * How much each new clock sample moves the estimate. Low, because the samples are noisy: each one
 * is the server's `Date.now()` at send time seen after an unknown one-way delay, so a single
 * sample is only ever "roughly right" and averaging beats trusting the latest.
 */
export const CLOCK_EMA_ALPHA = 0.2;

/**
 * How long to keep trying to rejoin after a drop, and how many tries that allows.
 *
 * The server holds a dropped seat for `RECONNECT_SECONDS` (600 s, scaled by `TIMER_SCALE`), so the
 * client should not be the one to give up first. 45 s covers a WiFi handover or a laptop waking,
 * and stops well short of a player staring at "Reconnecting…" for a table that is gone.
 */
export const RECONNECT_WINDOW_MS = 45_000;
export const MAX_RECONNECT_ATTEMPTS = 12;
/** Backoff ceiling: a long window must not become a long silence between tries. */
export const MAX_RECONNECT_DELAY_MS = 4_000;

/**
 * True when the server has refused the reconnection for good rather than transiently.
 *
 * Colyseus answers a spent or expired reservation with `MATCHMAKE_EXPIRED` (4212) and the text
 * "seat reservation expired". Both are checked: the code is the contract, the text is the thing
 * that actually shows up in a thrown `ServerError` across SDK versions.
 */
export function isSeatGone(err: unknown): boolean {
  const e = err as { code?: unknown; message?: unknown } | null;
  if (e && typeof e.code === 'number' && (e.code === 4212 || e.code === 524)) return true;
  return typeof e?.message === 'string' && /seat reservation|reservation expired|MATCHMAKE_EXPIRED/i.test(e.message);
}

/**
 * `?seed=<string>` pins the deal for a whole table so a test replays the same cards. The server
 * honors it only outside production (DESIGN-server room options), so this is safe to read from
 * the URL unconditionally.
 */
export function seedFromUrl(search: string = globalThis.location?.search ?? ''): string | undefined {
  const seed = new URLSearchParams(search).get('seed')?.trim();
  return seed ? seed.slice(0, 64) : undefined;
}

interface StringStorage {
  getItem(name: string): string | null;
  setItem(name: string, value: string): void;
  removeItem(name: string): void;
}

export interface ColyseusDriverOptions {
  surface: 'discord' | 'web';
  token?: string | null;
  endpoint?: string;
  instanceId?: string;
  guildId?: string | null;
  channelId?: string | null;
  fetchFn?: typeof fetch;
  /** Defaults to `sessionStorage`; pass null to disable reconnection tokens. */
  storage?: StringStorage | null;
  reconnectAttempts?: number;
  reconnectDelayMs?: number;
  /** How long to keep trying after a drop. See `RECONNECT_WINDOW_MS`. */
  reconnectWindowMs?: number;
  /** Injectable for tests. */
  client?: Client;
  roomName?: string;
  /**
   * Highest cue seq the app has actually APPLIED. `resync {sinceSeq}` uses this rather than the
   * highest received: cues queued behind an animation and then dropped by a flush were never
   * shown, and claiming them would lose them. Defaults to the received watermark.
   */
  appliedSeq?: () => number;
}

/**
 * Normalize a schema state (or its JSON) into a plain `TableView` with every field present.
 *
 * Each seat is spread over `emptySeatMirror()`, so a field the schema gained — `level` is the
 * latest — arrives mirrored without any work here, and a server that predates it still yields the
 * default (0, "no badge") rather than `undefined` reaching the scene.
 */
export function toTableView(raw: unknown): TableView {
  const json =
    raw && typeof (raw as { toJSON?: unknown }).toJSON === 'function'
      ? (raw as { toJSON: () => unknown }).toJSON()
      : raw;
  const base = emptyTableView();
  if (!json || typeof json !== 'object') return base;
  const j = json as Partial<TableView> & Record<string, unknown>;
  const view: TableView = { ...base, ...j } as TableView;
  view.seats = Array.isArray(j.seats) ? j.seats.map((s, i) => ({ ...base.seats[i]!, ...s })) : base.seats;
  view.teams = Array.isArray(j.teams) ? j.teams.map((t, i) => ({ ...(base.teams[i] ?? base.teams[0]!), ...t })) : base.teams;
  view.trick = Array.isArray(j.trick) ? j.trick.map((p) => ({ ...p })) : [];
  view.timer = { ...base.timer, ...(j.timer ?? {}) };
  view.rematchVotes = j.rematchVotes && typeof j.rematchVotes === 'object' ? { ...(j.rematchVotes as Record<string, string>) } : {};
  view.pendingSeats = j.pendingSeats && typeof j.pendingSeats === 'object' ? { ...(j.pendingSeats as Record<string, number>) } : {};
  return view;
}

function defaultStorage(): StringStorage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

export class ColyseusDriver implements TableDriver {
  readonly client: Client;
  private room: Room | null = null;
  private readonly opts: ColyseusDriverOptions;
  private readonly storage: StringStorage | null;
  private readonly fetchFn: typeof fetch;
  private readonly roomName: string;
  private readonly stateCbs = new Set<(v: TableView) => void>();
  private readonly cueCbs = new Set<(c: Cue) => void>();
  private readonly privateCbs = new Set<(v: SeatView) => void>();
  private readonly connCbs = new Set<(s: DriverConnection) => void>();
  private readonly resyncCbs = new Set<(k: ResyncKind) => void>();
  private readonly errorCbs = new Set<(code: string, msg: string, intent?: string) => void>();
  private readonly progressCbs = new Set<(msg: ProgressMessage) => void>();
  private lastSeq = -1;
  private leaving = false;
  private disposers: Array<() => void> = [];
  private _connection: DriverConnection | 'idle' | 'connecting' = 'idle';
  private _lostReason: string | null = null;
  /** `serverNow - Date.now()`, smoothed. 0 until the first sample, which is the honest default. */
  private clockOffset = 0;
  private clockSamples = 0;

  constructor(opts: ColyseusDriverOptions) {
    this.opts = opts;
    this.storage = opts.storage === undefined ? defaultStorage() : opts.storage;
    this.fetchFn = opts.fetchFn ?? ((input, init) => fetch(input, init));
    this.roomName = opts.roomName ?? 'table';
    this.client = opts.client ?? new Client(opts.endpoint ?? netOrigin());
    if (opts.token) this.client.auth.token = opts.token;
  }

  get connection(): DriverConnection | 'idle' | 'connecting' {
    return this._connection;
  }

  /** Why the connection ended, when the server gave a reason (currently only `replaced`). */
  get lostReason(): string | null {
    return this._lostReason;
  }

  /**
   * The server's clock, estimated. Timers arrive as server timestamps, so a device whose clock is
   * wrong would otherwise draw every ring as already drained; comparing against this instead of
   * `Date.now()` makes the drawing correct however far off the device is.
   */
  serverNow(): number {
    return Date.now() + this.clockOffset;
  }

  /** Difference between the two clocks in ms, positive when the server is ahead. */
  get clockSkew(): number {
    return this.clockOffset;
  }

  /**
   * Fold one `serverNow` sample into the estimate. The first sample is taken whole (any estimate
   * beats none); later ones move it slowly, so one slow round trip cannot swing the clock.
   */
  private sampleClock(serverNow: unknown): void {
    if (typeof serverNow !== 'number' || !Number.isFinite(serverNow)) return;
    const offset = serverNow - Date.now();
    this.clockOffset = this.clockSamples === 0 ? offset : this.clockOffset + CLOCK_EMA_ALPHA * (offset - this.clockOffset);
    this.clockSamples++;
  }

  get roomId(): string | null {
    return this.room?.roomId ?? null;
  }

  get sessionId(): string | null {
    return this.room?.sessionId ?? null;
  }

  /** Highest cue seq received (for `resync {sinceSeq}`). */
  get lastCueSeq(): number {
    return this.lastSeq;
  }

  setToken(token: string | null): void {
    if (token) this.client.auth.token = token;
  }

  async connect(opts: ConnectOptions = {}): Promise<void> {
    this.leaving = false;
    this._connection = 'connecting';
    const room = (await this.tryStoredReconnect()) ?? (await this.join(opts));
    this.attach(room);
  }

  /**
   * Boot-time resume (§3.5): a reload keeps `sessionStorage`, so the token from before the reload
   * puts us back in the same seat. Unlike `connect`, this NEVER creates or joins a new table, so
   * a stale token just clears itself and the player lands in the lobby.
   */
  async resume(): Promise<boolean> {
    if (this.room) return true;
    if (!this.storage?.getItem(RECONNECTION_STORAGE_KEY)) return false;
    this.leaving = false;
    this._connection = 'connecting';
    const room = await this.tryStoredReconnect();
    if (!room) {
      this._connection = 'idle';
      return false;
    }
    this.attach(room);
    return true;
  }

  private async tryStoredReconnect(): Promise<Room | null> {
    if (this.room) return null; // already attached (boot-time `resume` got there first)
    const token = this.storage?.getItem(RECONNECTION_STORAGE_KEY);
    if (!token) return null;
    try {
      const room = await this.client.reconnect(token);
      return room;
    } catch {
      this.storage?.removeItem(RECONNECTION_STORAGE_KEY);
      return null;
    }
  }

  private async join(opts: ConnectOptions): Promise<Room> {
    const seed = opts.seed ?? seedFromUrl();
    const seeded = <T extends object>(o: T): T => (seed ? { ...o, seed } : o);
    if (this.opts.surface === 'discord') {
      return this.client.joinOrCreate(this.roomName, {
        surface: 'discord',
        instanceId: this.opts.instanceId,
        guildId: this.opts.guildId ?? null,
        channelId: this.opts.channelId ?? null,
      });
    }
    if (opts.roomId) return this.client.joinById(opts.roomId, { surface: 'web' });
    if (opts.code) {
      const roomId = await this.lookupCode(opts.code);
      return this.client.joinById(roomId, { surface: 'web' });
    }
    if (opts.quickPlay) return this.client.create(this.roomName, seeded({ surface: 'web', solo: true }));
    if (opts.options) {
      const { roomId } = await this.createPrivate(opts.options, seed);
      return this.client.joinById(roomId, { surface: 'web' });
    }
    return this.client.create(this.roomName, seeded({ surface: 'web' }));
  }

  async lookupCode(code: string): Promise<string> {
    const res = await this.fetchFn(apiPath(`/api/rooms/${encodeURIComponent(code.toUpperCase())}`), {
      headers: this.authHeaders(),
    });
    if (!res.ok) throw new Error(res.status === 404 ? 'No table with that code' : `room lookup failed: HTTP ${res.status}`);
    const body = (await res.json()) as { roomId: string };
    return body.roomId;
  }

  async createPrivate(rules: ConnectOptions['options'], seed?: string): Promise<{ roomId: string; code: string }> {
    const res = await this.fetchFn(apiPath('/api/rooms'), {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...this.authHeaders() },
      body: JSON.stringify({ surface: 'web', private: true, rules, ...(seed ? { seed } : {}) }),
    });
    if (!res.ok) throw new Error(`create room failed: HTTP ${res.status}`);
    return (await res.json()) as { roomId: string; code: string };
  }

  private authHeaders(): Record<string, string> {
    const token = this.client.auth.token;
    return token ? { authorization: `Bearer ${token}` } : {};
  }

  private attach(room: Room): void {
    this.detach();
    this.room = room;
    // A new Room means a new cue stream position; carrying the old watermark would make the first
    // `resync` claim cues this driver never saw.
    this.lastSeq = -1;
    this.persistToken();

    const offs: Array<() => void> = [];
    const onState = (state: unknown) => this.emitState(state);
    room.onStateChange(onState);
    offs.push(() => room.onStateChange.remove(onState));
    offs.push(room.onMessage<Cue>('cue', (cue) => this.emitCue(cue)));
    offs.push(
      room.onMessage<PrivateMessage>('private', (v) => {
        this.sampleClock((v as { serverNow?: number }).serverNow);
        this.privateCbs.forEach((cb) => cb(v));
      }),
    );
    offs.push(room.onMessage<CuesMessage>('cues', (batch) => this.onCatchUp(batch)));
    offs.push(
      // Parsed rather than trusted, for the same reason the HTTP layer parses: this one drives an
      // animation with arithmetic in it (a bar from `xpBefore` to `xpAfter`), and a missing field
      // would render as `NaN` across the end screen. A malformed payload is dropped — no XP panel
      // is a far better end screen than a broken one, and the ledger row is already written.
      room.onMessage<unknown>('progress', (raw) => {
        const parsed = ProgressMessageSchema.safeParse(raw);
        if (!parsed.success) {
          console.warn('[net] unusable progress message', parsed.error.issues);
          return;
        }
        this.progressCbs.forEach((cb) => cb(parsed.data));
      }),
    );
    offs.push(
      room.onMessage<ErrorMessage>('error', (e) => {
        const code = String(e?.code ?? 'error');
        const msg = String(e?.msg ?? '');
        // The same player opened another tab and the server closed this one. Remember why, so the
        // close that follows is reported as "playing in another tab" rather than a lost connection.
        if (code === 'replaced') {
          this.leaving = true;
          this._lostReason = msg || 'You joined from another tab';
        }
        // `intent` names the client message this refuses, when the server knew it. Absent for the
        // two refusals that happen before the type is parsed and for `replaced`, which is not
        // about an intent at all.
        const intent = typeof e?.intent === 'string' ? e.intent : undefined;
        this.errorCbs.forEach((cb) => cb(code, msg, intent));
      }),
    );
    // No `emote` message handler, deliberately. An emote arrives as a real `cue`, with its own seq
    // and its place in the stream. This used to ALSO synthesise a cue from a separate `emote`
    // broadcast that carried the same event, so every sticker was handled twice: the bubble
    // restarted mid-flight and the pop sample played about 40 ms behind itself.
    const onDrop = () => this.setConnection('reconnecting');
    const onReconnect = () => {
      // The SDK reuses this Room object and the server issues a FRESH reconnection token on every
      // join, so the one stored at attach time is already spent. Without this, a silent reconnect
      // followed later by a reload sends a dead token and the player lands in the lobby while a
      // bot holds their seat.
      this.persistToken();
      this.resync();
      this.setConnection('connected');
    };
    // The SDK's own socket error, not a room verdict. It normally carries the websocket close
    // code; some paths invoke it with nothing at all, and `String(undefined)` reported a defect
    // called "undefined" that nobody could look up. `socket` is at least a name.
    const onError = (code?: number, message?: string) =>
      this.errorCbs.forEach((cb) => cb(typeof code === 'number' ? String(code) : SOCKET_ERROR_CODE, message ?? ''));
    const onLeave = (code: number) => {
      this.detach();
      // Colyseus closes with 4000 (consented) or 4001+ (shutdown, replaced); 1000 never arrives, so
      // testing for it left the client "Reconnecting…" against a room that was deliberately closed.
      if (this.leaving || DELIBERATE_CLOSE_CODES.includes(code)) {
        this.storage?.removeItem(RECONNECTION_STORAGE_KEY);
        this.setConnection('lost');
        return;
      }
      void this.manualReconnect();
    };
    room.onDrop(onDrop);
    room.onReconnect(onReconnect);
    room.onError(onError);
    room.onLeave(onLeave);
    offs.push(() => room.onDrop.remove(onDrop));
    offs.push(() => room.onReconnect.remove(onReconnect));
    offs.push(() => room.onError.remove(onError));
    offs.push(() => room.onLeave.remove(onLeave));
    this.disposers = offs;

    this.setConnection('connected');
    if (room.state) this.emitState(room.state);
    // Ask for anything missed. Harmless on a first join (`sinceSeq` -1 asks for everything, and the
    // server answers with a snapshot marker), essential after a reconnect.
    this.resync();
  }

  /** Store the room's CURRENT reconnection token. The server rotates it on every join. */
  private persistToken(): void {
    const token = this.room?.reconnectionToken;
    if (token) this.storage?.setItem(RECONNECTION_STORAGE_KEY, token);
  }

  private detach(): void {
    this.disposers.forEach((off) => off());
    this.disposers = [];
    this.room = null;
  }

  /**
   * Rejoin after a socket drop the SDK did not recover on its own.
   *
   * Bounded by TIME, not by a count of tries. Three attempts at 1 s, 2 s and 3 s gave up after six
   * seconds, while the server holds the seat for `RECONNECT_SECONDS` — ten minutes in production,
   * a minute even at the e2e timer scale. A player whose WiFi took eight seconds to come back was
   * therefore dropped into the lobby while a bot took a seat that was still theirs, and the e2e
   * blip test saw the same thing as `connection: 'lost'` with a run of socket errors behind it.
   * Backoff is capped so a long window does not become a long silence between tries.
   */
  private async manualReconnect(): Promise<void> {
    const token = this.storage?.getItem(RECONNECTION_STORAGE_KEY);
    const attempts = this.opts.reconnectAttempts ?? MAX_RECONNECT_ATTEMPTS;
    const delay = this.opts.reconnectDelayMs ?? 1000;
    const windowMs = this.opts.reconnectWindowMs ?? RECONNECT_WINDOW_MS;
    const startedAt = Date.now();
    if (token) {
      this.setConnection('reconnecting');
      for (let i = 0; i < attempts && !this.leaving; i++) {
        try {
          const room = await this.client.reconnect(token);
          this.attach(room);
          this.resync();
          return;
        } catch (err) {
          // The server has given the seat away, or never had this reservation. Another try cannot
          // succeed, and pretending otherwise leaves the player watching a spinner for the whole
          // window instead of being told and offered the lobby.
          if (isSeatGone(err)) break;
          if (Date.now() - startedAt >= windowMs) break;
          await new Promise((r) => setTimeout(r, Math.min(delay * (i + 1), MAX_RECONNECT_DELAY_MS)));
        }
      }
      this.storage?.removeItem(RECONNECTION_STORAGE_KEY);
    }
    this.setConnection('lost');
  }

  private resync(): void {
    const applied = this.opts.appliedSeq?.();
    // Never ask from ahead of what was shown, and never from ahead of what arrived. Clamped at 0
    // because the protocol takes a non-negative watermark, and 0 already means "everything": the
    // server's cue seqs start at 1.
    const since = Math.max(0, applied === undefined ? this.lastSeq : Math.min(applied, this.lastSeq));

    // Rewind our own position to what we just asked for, BEFORE the answer arrives.
    //
    // `emitCue` drops anything at or below `lastSeq` so a duplicate is never shown twice. Asking
    // from the applied watermark means the server deliberately replays cues we received but never
    // got to show; leaving `lastSeq` at the received position would make that filter throw every
    // one of them away, and the queue would sit at the old watermark for the rest of the session.
    this.lastSeq = since;
    this.send('resync', { sinceSeq: since });
  }

  private onCatchUp(batch: CuesMessage): void {
    this.sampleClock((batch as { serverNow?: number }).serverNow);
    if (batch.snapshot) {
      if (batch.cueSeq > this.lastSeq) this.lastSeq = batch.cueSeq;
      this.resyncCbs.forEach((cb) => cb('snapshot'));
      return;
    }
    for (const cue of batch.cues) if (cue.seq > this.lastSeq) this.emitCue(cue);
    this.resyncCbs.forEach((cb) => cb('cues'));
  }

  private emitState(state: unknown): void {
    const view = toTableView(state);
    this.stateCbs.forEach((cb) => cb(view));
  }

  private emitCue(cue: Cue): void {
    if (cue.seq > this.lastSeq) this.lastSeq = cue.seq;
    this.cueCbs.forEach((cb) => cb(cue));
  }

  private setConnection(s: DriverConnection): void {
    if (this._connection === s) return;
    this._connection = s;
    this.connCbs.forEach((cb) => cb(s));
  }

  send<K extends keyof Intents>(intent: K, payload: Intents[K]): void {
    if (!this.room) {
      console.warn(`[net] send(${intent}) with no room`);
      return;
    }
    this.room.send(intent, payload);
  }

  onState(cb: (view: TableView) => void): () => void {
    this.stateCbs.add(cb);
    return () => this.stateCbs.delete(cb);
  }

  onCue(cb: (cue: Cue) => void): () => void {
    this.cueCbs.add(cb);
    return () => this.cueCbs.delete(cb);
  }

  onPrivate(cb: (v: SeatView) => void): () => void {
    this.privateCbs.add(cb);
    return () => this.privateCbs.delete(cb);
  }

  onConnection(cb: (s: DriverConnection) => void): () => void {
    this.connCbs.add(cb);
    return () => this.connCbs.delete(cb);
  }

  onResync(cb: (kind: ResyncKind) => void): () => void {
    this.resyncCbs.add(cb);
    return () => this.resyncCbs.delete(cb);
  }

  onError(cb: (code: string, msg: string, intent?: string) => void): () => void {
    this.errorCbs.add(cb);
    return () => this.errorCbs.delete(cb);
  }

  onProgress(cb: (msg: ProgressMessage) => void): () => void {
    this.progressCbs.add(cb);
    return () => this.progressCbs.delete(cb);
  }

  leave(): void {
    this.leaving = true;
    const room = this.room;
    this.detach();
    this.storage?.removeItem(RECONNECTION_STORAGE_KEY);
    if (room) void room.leave(true).catch(() => {});
    this.setConnection('lost');
  }
}
